import { useEffect, useState } from 'react';

/**
 * Debounces a value by a specified delay.
 *
 * @template T - The type of the value being debounced.
 * @param value - The value to debounce.
 * @param delay - Delay in milliseconds.
 * @returns The debounced value, updated only after `delay` ms of inactivity.
 *
 * @example
 * const debouncedQuery = useDebounce(searchQuery, 300);
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
